/**
 * React hook for fetching the current organization's plan name for display.
 *
 * Derives the badge label from the org-scoped `org_billing` row (read via
 * `OrgProvider`), the same source the entitlement checks use. Every member of
 * an org can read that row through RLS, so the badge shows the correct tier for
 * any role. The earlier implementation went through the
 * `organization:view_billing`-gated metering endpoint, which 403s for non-owner
 * members (e.g. editors) and silently fell back to "Free".
 *
 * @module lib/usePlanBadge
 */
import { useOrg } from './OrgProvider';
import { getPlanDisplayName } from './billing';

/** Return type for the usePlanBadge hook. */
interface UsePlanBadgeReturn {
  /** Plan display name (Free/Pro/Business), or null while loading. */
  planName: string | null;
  /** Whether the org/billing data is still loading. */
  loading: boolean;
}

/**
 * Returns the current org's plan display name. Resolves the Stripe product id
 * stored on the active org's `org_billing` row to a tier name, defaulting to
 * "Free" when there is no plan id. `planName` stays null until both the core
 * org context and the billing row have loaded, so the badge does not flash
 * "Free" before the real tier resolves.
 */
export function usePlanBadge(): UsePlanBadgeReturn {
  const { orgBilling, isLoading, billingLoading } = useOrg();
  const loading = isLoading || billingLoading;
  const planName = loading
    ? null
    : getPlanDisplayName(orgBilling?.plan_id ?? undefined);
  return { planName, loading };
}
